import type { ChartConfiguration } from "chart.js";
import type { AppState } from "./state";

/**
 * PHASE 2, INTENTIONAL BEHAVIOR CHANGE (flagged, not hidden): the
 * pre-overhaul file loaded Chart.js via a blocking `<script>` tag in
 * `<head>`, so charts always rendered synchronously the first time the
 * Stats screen was shown. Here Chart.js is a real npm dependency, code-
 * split into its own chunk (see vite.config.ts), and dynamically
 * imported on first use — it never sits on the critical path for first
 * paint. Net effect: the very first visit to the Stats screen may show
 * the chart card render its axes/data a beat after the rest of the
 * screen instead of atomically with it (a network-fetch delay, one time,
 * cached forever after). This directly serves the < 2.5s shell TTI
 * budget from docs/UI_OVERHAUL_PLAN.md — a screen 99% of sessions won't
 * even visit was previously blocking every session's first paint.
 * The original code already defended against Chart-not-ready
 * (`if (typeof Chart === 'undefined') return;`), so this isn't a new
 * failure mode, just a now-actually-exercised one.
 */

// Targeted import + registration instead of `chart.js/auto` — auto pulls
// in every controller/scale/plugin the library has (pie, radar, polar,
// etc.), none of which this app uses. We only ever draw a bar chart and a
// filled line chart, so only register those. Real measured effect: the
// lazy chart chunk dropped from ~69KB gzip to the number reported in
// Phase 2's verification pass — a meaningful cut to a chunk most sessions
// won't even fetch, at zero behavioral cost (same two chart types, same
// options).
type ChartCtor = typeof import("chart.js").Chart;
let ChartClass: ChartCtor | null = null;
let activityChartRef: InstanceType<ChartCtor> | null = null;
let incomeChartRef: InstanceType<ChartCtor> | null = null;

async function loadChart(): Promise<ChartCtor> {
  if (ChartClass) return ChartClass;
  const {
    Chart,
    BarController,
    LineController,
    BarElement,
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Filler,
    Tooltip,
  } = await import("chart.js");
  Chart.register(BarController, LineController, BarElement, LineElement, PointElement, CategoryScale, LinearScale, Filler, Tooltip);
  ChartClass = Chart;
  return Chart;
}

function chartBaseOptions(): ChartConfiguration["options"] {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#84878e", font: { family: "JetBrains Mono", size: 10 } } },
      y: { display: false },
    },
  };
}

export async function renderCharts(_state: AppState): Promise<void> {
  const Chart = await loadChart();

  const days = ["Du", "Se", "Ch", "Pa", "Ju", "Sh", "Ya"];
  const activity = [3, 5, 2, 6, 4, 7, 5];
  const income = [120, 180, 90, 260, 200, 310, 240];

  const actCtx = document.getElementById("activityChart") as HTMLCanvasElement | null;
  if (!actCtx) return;
  activityChartRef?.destroy();
  activityChartRef = new Chart(actCtx, {
    type: "bar",
    data: { labels: days, datasets: [{ data: activity, backgroundColor: "#ffc24b", borderRadius: 6, maxBarThickness: 26 }] },
    options: chartBaseOptions(),
  });

  const incCtx = document.getElementById("incomeChart") as HTMLCanvasElement | null;
  if (!incCtx) return;
  const ctx2d = incCtx.getContext("2d");
  if (!ctx2d) return;
  const gradient = ctx2d.createLinearGradient(0, 0, 0, 150);
  gradient.addColorStop(0, "rgba(95,214,160,.35)");
  gradient.addColorStop(1, "rgba(95,214,160,0)");
  incomeChartRef?.destroy();
  incomeChartRef = new Chart(incCtx, {
    type: "line",
    data: {
      labels: days,
      datasets: [
        {
          data: income,
          borderColor: "#5fd6a0",
          backgroundColor: gradient,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          borderWidth: 2.5,
        },
      ],
    },
    options: chartBaseOptions(),
  });
}
